import type {
  AssetActivityLogRow,
  AssetActivityLogUser,
} from "../labAssets.types";
import { AssetValidationColumnDefinitions } from "@/Features/AssetValidations/Tables/AssetValidationColumnDefinitions";
import { LabAssetColumnDefinitions } from "../Tables/LabAssetColumnDefinitions";

export type AssetHistoryKind =
  | "created"
  | "issued_to"
  | "location"
  | "updated"
  | "deleted"
  | "validation"
  | "attachment"
  | "other";

export interface AssetHistoryEntry {
  kind: AssetHistoryKind;
  label: string;
  detail: string;
}

// "a, b" (api column names) → table captions; an unknown column keeps its
// raw name.
const captionLookup = (
  columns: Array<{ dataField: string; caption?: string }>
) => {
  const captions = new Map(columns.map((col) => [col.dataField, col.caption]));
  return (fields: string) =>
    fields
      .split(", ")
      .map((field) => captions.get(field) ?? field)
      .join(", ");
};

const toAssetCaptions = captionLookup(LabAssetColumnDefinitions);
const toValidationCaptions = captionLookup(AssetValidationColumnDefinitions);

// The api writes `event`/`event_data` as null for every asset writer, so the
// message text is the only signal. These patterns pin the exact strings from
// monolith-api assets.service.ts, validations.service.ts and
// asset-attachments.service.ts; a reworded message falls through to "other"
// and still renders as raw text.
const RULES: Array<[RegExp, (match: RegExpMatchArray) => AssetHistoryEntry]> = [
  [
    /^(?:Equipment|Software) Asset Created(?: - (.*))?$/,
    (m) => ({ kind: "created", label: "Created", detail: m[1] ?? "" }),
  ],
  [
    /^Asset Issued To changed: (.*)$/,
    (m) => ({ kind: "issued_to", label: "Issued To", detail: m[1] }),
  ],
  [
    // Keeps the "from X to Y" tail whole: splitting it would misread a
    // location name that contains " to ".
    /^Asset Location Changed - .*?, (from .* to .*)$/,
    (m) => ({ kind: "location", label: "Location", detail: m[1] }),
  ],
  [
    /^Asset Item Updated - fields: (.*)$/,
    (m) => {
      // A software rename sends `license_name` plus `name: null`
      // (toUpdateBody) for the form's one Name field, so show it once.
      const fields = m[1].split(", ");
      const shown = fields.includes("name")
        ? fields.filter((field) => field !== "license_name")
        : fields;
      return {
        kind: "updated",
        label: "Updated",
        detail: toAssetCaptions(shown.join(", ")),
      };
    },
  ],
  [
    /^Asset Item Deleted$/,
    () => ({ kind: "deleted", label: "Deleted", detail: "" }),
  ],
  [
    /^Asset Validation Updated - fields: (.*)$/,
    (m) => ({
      kind: "validation",
      label: "Validation",
      detail: `Updated - ${toValidationCaptions(m[1])}`,
    }),
  ],
  [
    // Drops the trailing "(validation <uuid>)" the delete marker carries.
    /^Asset Validation (.*?)(?: \(validation [^)]+\))?$/,
    (m) => ({ kind: "validation", label: "Validation", detail: m[1] }),
  ],
  [
    /^Asset Attachment (.*)$/,
    (m) => ({ kind: "attachment", label: "Attachment", detail: m[1] }),
  ],
];

export const parseAssetHistoryEntry = (
  row: Pick<AssetActivityLogRow, "activity">
): AssetHistoryEntry => {
  for (const [pattern, toEntry] of RULES) {
    const match = row.activity.match(pattern);
    if (match) return toEntry(match);
  }
  return { kind: "other", label: "Activity", detail: row.activity };
};

export const getHistoryUserName = (user?: AssetActivityLogUser | null) =>
  user?.full_name || user?.email || "Unknown User";
